import styled, { css } from 'styled-components';
import { baseVariables } from '../../styles';
import { OTabContainerAlignment } from './OTabContainer.types';

const { spacers, mediaQuery } = baseVariables;

const singleLineClip = css`
  display: block;
  max-inline-size: 100%;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
`;

const multilineClamp = (lines: number) => css`
  display: -webkit-box;
  max-inline-size: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
`;

/**
 * Wrapper props:
 *  - `$alignment`      Single source of truth for the organism's alignment token. Used by the
 *                      mobile-override rules below to decide whether centered styles should be
 *                      reinstated at `>= md` breakpoints.
 *  - `$equalTabWidths` When true, all tab slides share the same width at every breakpoint. The
 *                      organism measures the widest tab button in JS and writes the result
 *                      to the `--o-tab-equal-width` custom property on this wrapper; the rule
 *                      below applies that width to every `.swiper-slide` regardless of viewport.
 */
export const Wrapper = styled.div<{ $alignment?: OTabContainerAlignment; $equalTabWidths?: boolean }>`
  display: flex;
  flex-direction: column;
  inline-size: 100%;
  gap: ${spacers?.spacers?.[4] ?? '1.5rem'};
  padding-block-start: ${spacers?.spacers?.[4] ?? '1.5rem'};

  @media ${mediaQuery.md} {
    padding-block-start: ${spacers?.spacers?.[5] ?? '2rem'};
  }

  /*
   * MContentHeader ships with its own bottom padding for standalone use. When composed
   * inside OTabContainer the gap between MContentHeader and MTabGroup is owned by the
   * organism's flex \`gap\` above, so we neutralize the molecule's trailing padding here.
   */
  [data-component-name='m-web-ui-kit-MContentHeader'] {
    padding-block-end: 0;
  }

  [data-component-name='a-web-ui-kit-AEyebrow'] {
    ${singleLineClip}
  }

  [data-component-name='a-web-ui-kit-AHeader'] {
    padding-block-end: ${spacers?.spacers?.[2] ?? '0.5rem'};
  }

  [data-component-name='a-web-ui-kit-AHeader'],
  [data-component-name='a-web-ui-kit-ABody'] {
    ${multilineClamp(3)}
  }

  [data-component-name='m-web-ui-kit-MTabGroup'] .swiper-slide,
  [data-component-name='m-web-ui-kit-MTabControl'] {
    max-inline-size: 100%;
  }

  [data-component-name='m-web-ui-kit-MTabControl'] .m-tab-control-label {
    ${singleLineClip}
  }

  /*
   * Mobile tab-group alignment rules.
   *
   * On mobile the Swiper strip must always hug the inline-start edge regardless of the
   * organism's \`alignment\` token. Two layers need overriding:
   *
   *  1. The tablist's \`justify-content\` — MTabGroup sets this to \`center\` when the
   *     alignment is Centered, which shifts the strip away from the leading edge.
   *  2. The SwiperWrapper / .swiper \`width\` — MTabGroup sets these to \`auto\` for
   *     centered mode, which shrinks the strip to content width and magnifies the centering.
   *
   * Both are reset here unconditionally (no alignment check) so the mobile tab row is
   * always left-anchored. At \`>= md\` the centered values are reinstated in the block below.
   *
   *  3. The inner MTabControl button and its text block are forced to \`flex-start\` so
   *     individual tab text hugs the leading edge even on a centered organism.
   */
  [data-component-name='m-web-ui-kit-MTabGroup'] [role='tablist'] {
    justify-content: flex-start;
  }

  [data-component-name='m-web-ui-kit-MTabGroup'] .swiper,
  [data-component-name='m-web-ui-kit-MTabGroup'] > div > div:has(> .swiper) {
    inline-size: 100%;
    max-inline-size: 100%;
  }

  [data-component-name='m-web-ui-kit-MTabControl'],
  [data-component-name='m-web-ui-kit-MTabControl'] > div {
    align-items: flex-start;
  }

  /*
   * Equal-width tabs — all breakpoints.
   * When \`$equalTabWidths\` is true, every \`.swiper-slide\` is pinned to the widest tab's
   * measured width (\`--o-tab-equal-width\`, written by the organism in JS). Applies at
   * every viewport size so mobile and desktop behave consistently.
   */
  ${({ $equalTabWidths }) =>
    $equalTabWidths &&
    `
    --o-tab-equal-ready: 0;

    [data-component-name='m-web-ui-kit-MTabGroup'] [role='tablist'] {
      opacity: var(--o-tab-equal-ready);
      transition: opacity 0.1s ease-out;
    }

    [data-component-name='m-web-ui-kit-MTabGroup'] .swiper-slide {
      inline-size: var(--o-tab-equal-width);
      flex-shrink: 0;
    }

    [data-component-name='m-web-ui-kit-MTabControl'] {
      inline-size: 100%;
    }
  `}

  /*
   * At \`>= md\` viewports:
   *  - Re-enable centered layout when the organism asks for it.
   */
  @media ${mediaQuery.md} {
    [data-component-name='a-web-ui-kit-AHeader'],
    [data-component-name='a-web-ui-kit-ABody'] {
      ${multilineClamp(2)}
    }

    ${({ $alignment, $equalTabWidths }) =>
      ($alignment === OTabContainerAlignment.Centered || $alignment === 'Center') &&
      `
      [data-component-name='m-web-ui-kit-MTabGroup'] [role='tablist'] {
        justify-content: center;
      }

      ${
        !$equalTabWidths
          ? `
      [data-component-name='m-web-ui-kit-MTabGroup'] > div > div:has(> .swiper),
      [data-component-name='m-web-ui-kit-MTabGroup'] .swiper {
        inline-size: auto;
      }
      `
          : ''
      }

      [data-component-name='m-web-ui-kit-MTabControl'],
      [data-component-name='m-web-ui-kit-MTabControl'] > div {
        align-items: center;
      }
    `}
  }
`;
